import { Action } from '@reduxjs/toolkit';
import { call, put, select } from 'redux-saga/effects';
import i18n from 'i18n';
import { CaptionsSpokenLanguage } from 'features/application/types';
import { selectIsBreakoutRoom } from 'features/breakout-rooms/breakoutRoomsSlice';
import { FeatureFlag } from 'features/feature-set/types';
import { openModal } from 'features/modal/modalSlice';
import { addActionNotification } from 'features/notifications/action-notifications/addActionNotification';
import { ActionNotificationParams } from 'features/notifications/types';
import { ROOM_SETTINGS_CAPTIONS_TAB_INDEX } from 'features/transcripts/constants';
import { closeTranscriptionNotificationSaga } from 'features/transcripts/sagas/closeTranscriptionNotificationSaga';
import {
  selectCaptionsSettingsAvailable,
  selectCaptionsSpokenLanguage,
} from 'features/transcripts/slices/captions/captionsSlice';
import { selectTranscriptionStarted } from 'features/transcripts/slices/transcription/transcriptionSlice';
import { getCaptionsLanguageLabel } from 'features/transcripts/utils/getCaptionsLanguageLabel';

// A language that applies to everyone describes the session; one the participant picked for
// themselves describes only their own speech.
type AnnouncementVariant = 'session' | 'personal';

const contentKeys = {
  session: {
    withSettings: 'notifications:transcription_language.session',
    withoutSettings: 'notifications:transcription_language.session_without_settings',
  },
  personal: {
    withSettings: 'notifications:transcription_language.personal',
    withoutSettings: 'notifications:transcription_language.personal_without_settings',
  },
} as const;

export function* announceTranscriptionLanguageSaga(variant: AnnouncementVariant) {
  const transcriptionStarted: boolean = yield select(selectTranscriptionStarted);
  if (!transcriptionStarted) {
    return;
  }

  // A breakout room is not transcribed, so there is no language to name.
  const isBreakoutRoom: boolean = yield select(selectIsBreakoutRoom);
  if (isBreakoutRoom) {
    return;
  }

  // Must stay ahead of the notification below, which carries the very scope this clears.
  yield call(closeTranscriptionNotificationSaga);

  const captionsSettingsAvailable: boolean = yield select(selectCaptionsSettingsAvailable);
  const spokenLanguage: CaptionsSpokenLanguage = yield select(selectCaptionsSpokenLanguage);
  const language: string = yield call(getCaptionsLanguageLabel, spokenLanguage);

  const contentKey =
    contentKeys[variant][captionsSettingsAvailable ? 'withSettings' : 'withoutSettings'];

  const notificationParams: ActionNotificationParams = {
    content: i18n.t(contentKey, { language }),
    scope: [FeatureFlag.captions],
    ...(captionsSettingsAvailable && {
      okText: i18n.t('notifications:transcription_language.btn_ok_label'),
      okAction: openModal('roomSettings', { activeTab: ROOM_SETTINGS_CAPTIONS_TAB_INDEX }),
    }),
  };

  // No fixed toastId: react-toastify keeps a dismissed toast in its map until the exit animation
  // ends and drops a re-add of that id, so reusing one would swallow this notification.
  const notificationThunk: Action = yield call(addActionNotification, notificationParams);

  yield put(notificationThunk);
}
